import * as React from "react";
import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { BidQuestionGroupDto } from "lib/hooks/api/bid/bid_question/useBidQuestions";
import { useUpsertBidReportQuestions } from "lib/hooks/api/bid/bid_question/useUpsertBidReportQuestions";
import { EventNames, useTracking } from "lib/tracking";
import CollapseGroup from "../../ui/collapse_group/CollapseGroup";
import { BidQuestionGroup, BidSelfServeForm } from "./BidQuestionGroup";
import { DiscardSelfServeModal } from "./DiscardSelfServeModal";

type Props = {
  defaultValues: BidQuestionGroupDto[];
  fromBidQualification: boolean;
  hasChanged: boolean;
  setHasChanged: (hasChanged: boolean) => void;
};
function BidQuestionsForm({
  defaultValues,
  fromBidQualification,
  hasChanged,
  setHasChanged,
}: Props): JSX.Element {
  const { logEvent } = useTracking();
  const [openDiscardModal, setOpenDiscardModal] = useState(false);

  const { control, handleSubmit, reset } = useForm<BidSelfServeForm>({
    mode: "onChange",
    defaultValues: {
      questionGroups: defaultValues,
    },
  });

  const { mutate: upsertBidReportQuestions } = useUpsertBidReportQuestions({
    onSuccess({ upsertBidReportQuestions }) {
      if (!hasChanged) {
        setHasChanged(true);
      }
      // Reset the form to the new default values
      reset(
        {
          questionGroups: upsertBidReportQuestions.groups,
        },
        {
          keepDirty: false,
        },
      );
    },
  });

  const { fields: allBidQuestionGroups } = useFieldArray({
    control,
    name: "questionGroups",
  });

  const onSubmit = (formVals: BidSelfServeForm) => {
    // Map each group to update the order (if re-ordering has occured)
    const updatedGroups = formVals.questionGroups.map((group) => ({
      ...group,
      questions: group.questions.map((q, idx) => ({
        ...q,
        order: idx + 1, // order matches index + 1
      })),
    }));

    // Flatten new bid questions
    const newQuestions = updatedGroups.flatMap((group) => group.questions);

    upsertBidReportQuestions({
      req: {
        questions: newQuestions,
      },
    });

    logEvent(EventNames.bidSelfServeSaved, {
      "Context source": "Bid self-serve",
      Action: "Save",
      "Team ID": window.currentUser?.team.id,
      "User ID": window.currentUser?.guid,
    });
  };

  const submitForm = async () => {
    await handleSubmit(onSubmit)();
  };

  const collapsibleItems = allBidQuestionGroups.map((group) => ({
    header: group.name,
    content: (
      <BidQuestionGroup
        key={group.name}
        groupName={group.name}
        control={control}
        groupIndex={allBidQuestionGroups.findIndex((g) => g.name === group.name)}
        submitForm={submitForm}
      />
    ),
    key: group.name,
    chevronText: getQuestionGroupSubtitle(group.questions),
  }));

  return (
    <Form onSubmit={submitForm}>
      <BidQuestionsContainer fromBidQualification={fromBidQualification}>
        <TitleContainer>
          <Text h3>Manage bid / no-bid criteria</Text>
        </TitleContainer>
        <Flex flexDirection="column">
          <CollapseGroup items={collapsibleItems} showFullBorder showDivider />
        </Flex>
      </BidQuestionsContainer>

      <DiscardSelfServeModal
        isOpen={openDiscardModal}
        closeModal={() => setOpenDiscardModal(false)}
        discardChanges={() => {
          reset({ questionGroups: defaultValues });
          setOpenDiscardModal(false);
        }}
      />
    </Form>
  );
}

const Form = styled.form(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.colors.sysBackgroundDefault,
}));

const BidQuestionsContainer = styled(Flex)<{ fromBidQualification: boolean }>(
  ({ fromBidQualification }) => ({
    flexDirection: "column",
    marginBottom: 24,
    marginTop: 16,
    marginLeft: fromBidQualification ? 48 : 24,
    marginRight: fromBidQualification ? 48 : 24,
    minHeight: 100,
    maxWidth: 968,
  }),
);

const TitleContainer = styled(Flex)(({ theme }) => ({
  padding: 16,
  paddingLeft: 24,
  border: `1px solid ${theme.colors.sysBorderPrimary}`,
  borderBottom: "none",
}));

function getQuestionGroupSubtitle(questions: BidQuestionGroupDto["questions"]) {
  // The default questions are the ones that have null teamId
  const defaultQuestions = questions.filter((q) => q.teamId === null);
  const customQuestionsAmount = questions.length - defaultQuestions.length;
  if (customQuestionsAmount > 0) {
    return `${defaultQuestions.length} default criteria, ${customQuestionsAmount} custom criteria`;
  }
  return `${defaultQuestions.length} default criteria`;
}

export default BidQuestionsForm;
